import { readFile } from 'node:fs/promises';
import { Command } from 'commander';
import type { VaultItemType, VaultSensitivity } from '../../types/vault.types.js';
import { VaultProjectContextService } from '../../services/vault-project-context.service.js';
import { parseFields, redactVaultItem, unlockVault } from './shared.js';

interface AddOptions {
    title?: string;
    field?: string[];
    body?: string;
    file?: string;
    jsonFile?: string;
    sensitivity?: VaultSensitivity;
    secret?: boolean;
    tag?: string[];
    keyword?: string[];
    projectTag?: boolean;
    passphrase?: string;
    recoveryKey?: string;
    json?: boolean;
}

const ITEM_TYPES: VaultItemType[] = ['server', 'app', 'context', 'runbook', 'prompt', 'instruction', 'note', 'secret'];

export function createVaultAddCommand(): Command {
    return new Command('add')
        .description('Add an encrypted vault item')
        .argument('<type>', `Item type: ${ITEM_TYPES.join(', ')}`)
        .option('--title <value>', 'Item title')
        .option('--field <key=value>', 'Structured field; repeatable', (value, previous: string[] = []) => [...previous, value], [])
        .option('--body <text>', 'Text body')
        .option('--file <path>', 'Read body from file')
        .option('--json-file <path>', 'Read structured JSON body from file')
        .option('--sensitivity <level>', 'public | internal | secret', 'internal')
        .option('--secret', 'Mark item as containing secrets')
        .option('--no-secret', 'Mark item as safe to show without reveal reason')
        .option('--tag <tag>', 'Add tag; repeatable', (value, previous: string[] = []) => [...previous, value], [])
        .option('--no-project-tag', 'Do not add detected Jai1 project tags')
        .option('--keyword <keyword>', 'Add keyword; repeatable', (value, previous: string[] = []) => [...previous, value], [])
        .option('--passphrase <value>', 'Vault passphrase')
        .option('--recovery-key <value>', 'Recovery key')
        .option('-j, --json', 'Output JSON')
        .action(async function (this: Command, type: VaultItemType, options: AddOptions) {
            if (!ITEM_TYPES.includes(type)) {
                throw new Error(`Unsupported item type "${type}". Use one of: ${ITEM_TYPES.join(', ')}`);
            }
            const title = options.title || type;
            const { store, vmk } = await unlockVault(options);

            let body: Record<string, unknown> | string = parseFields(options.field);
            if (options.jsonFile) {
                body = JSON.parse(await readFile(options.jsonFile, 'utf-8')) as Record<string, unknown>;
            } else if (options.file) {
                body = await readFile(options.file, 'utf-8');
            } else if (options.body) {
                body = options.body;
            }

            const projectContextService = new VaultProjectContextService();
            const includeProjectTags = options.projectTag !== false;
            const projectContext = includeProjectTags ? projectContextService.detect() : null;
            const tags = projectContextService.buildItemTags(options.tag || [], { includeProjectTags });
            const item = await store.savePlainItem(vmk, {
                type,
                title,
                body,
                sensitivity: options.sensitivity,
                containsSecret: options.secret,
                tags,
                keywords: options.keyword || [],
            });

            const useJson = !!(options.json || this.optsWithGlobals().json);
            if (useJson) {
                console.log(JSON.stringify({ item: redactVaultItem(item) }, null, 2));
                return;
            }

            console.log(`Saved vault item: ${item.id}`);
            console.log(`Tags: ${item.tags.join(', ') || 'none'}`);
            if (projectContext) {
                console.log(`Project tags: ${projectContext.tags.join(', ')}`);
            }
        });
}
